import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

export interface PlaylistReaction {
  id: string;
  username: string;
  comment: string;
  points: number;
  created_at?: string;
}

export interface PlaylistTrack {
  submissionId: string;
  roundId: string;
  leagueId?: string;
  title: string;
  artist: string;
  youtubeId: string;
  submitter: string;
  comment?: string;
  thumbnail?: string;
  roundNumber?: number;
  reactions?: PlaylistReaction[];
  fun_facts?: string[];
}

interface PlaybackState {
  tracks: PlaylistTrack[];
  order: number[];
  orderIndex: number;
  isPlaying: boolean;
  isShuffled: boolean;
  playlistId: string | null;
  playlistLabel?: string;
}

interface SetPlaylistOptions {
  id: string;
  label?: string;
  startTrackId?: string;
  autoplay?: boolean;
  shuffle?: boolean;
}

interface PlaybackContextValue {
  state: PlaybackState;
  currentTrack: PlaylistTrack | null;
  setPlaylist: (tracks: PlaylistTrack[], options: SetPlaylistOptions) => void;
  playTrack: (trackId: string) => void;
  nextTrack: (forcePlay?: boolean) => void;
  previousTrack: (forcePlay?: boolean) => void;
  setOrderIndex: (orderIndex: number, forcePlay?: boolean) => void;
  togglePlay: () => void;
  setPlaying: (playing: boolean) => void;
  toggleShuffle: () => void;
  progressSeconds: number;
  durationSeconds: number;
  updatePlaybackMeta: (meta: { progressSeconds?: number; durationSeconds?: number }) => void;
}

const DEFAULT_STATE: PlaybackState = {
  tracks: [],
  order: [],
  orderIndex: 0,
  isPlaying: false,
  isShuffled: false,
  playlistId: null,
  playlistLabel: undefined,
};

const PlaybackContext = createContext<PlaybackContextValue | undefined>(undefined);

function generateSequentialOrder(length: number): number[] {
  return Array.from({ length }, (_, index) => index);
}

function generateShuffleOrder(length: number, currentIndex: number): number[] {
  const order = generateSequentialOrder(length).filter((idx) => idx !== currentIndex);
  for (let i = order.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [order[i], order[j]] = [order[j], order[i]];
  }
  return [currentIndex, ...order];
}

export function PlaybackProvider({ children }: { children: ReactNode }) {
  // Use simple useState - the original working pattern
  const [state, setState] = useState<PlaybackState>(DEFAULT_STATE);
  const [progressSeconds, setProgressSeconds] = useState(0);
  const [durationSeconds, setDurationSeconds] = useState(0);

  const currentTrackIndex = useMemo(() => {
    if (state.order.length === 0) {
      return null;
    }
    return state.order[state.orderIndex] ?? null;
  }, [state.order, state.orderIndex]);

  const currentTrack = useMemo(() => {
    if (currentTrackIndex == null) {
      return null;
    }
    return state.tracks[currentTrackIndex] ?? null;
  }, [state.tracks, currentTrackIndex]);

  const setPlaylist = useCallback(
    (tracks: PlaylistTrack[], options: SetPlaylistOptions) => {
      console.log('[Playback] setPlaylist called', { trackCount: tracks.length, options });
      if (tracks.length === 0) {
        console.log('[Playback] Empty tracks, resetting state');
        setState(DEFAULT_STATE);
        setProgressSeconds(0);
        setDurationSeconds(0);
        return;
      }

      const sequentialOrder = generateSequentialOrder(tracks.length);
      const startIndex = options.startTrackId
        ? tracks.findIndex((track) => track.submissionId === options.startTrackId)
        : 0;
      const safeStartIndex = startIndex >= 0 ? startIndex : 0;
      const shouldShuffle = Boolean(options.shuffle);
      const order = shouldShuffle
        ? generateShuffleOrder(tracks.length, safeStartIndex)
        : sequentialOrder;
      const orderIndex = shouldShuffle
        ? 0
        : sequentialOrder.findIndex((idx) => idx === safeStartIndex);

      const newState = {
        tracks,
        order,
        orderIndex,
        isPlaying: options.autoplay ?? false,
        isShuffled: shouldShuffle,
        playlistId: options.id,
        playlistLabel: options.label,
      };
      console.log('[Playback] Setting new state', { trackCount: tracks.length, isPlaying: newState.isPlaying });
      setState(newState);
    },
    [],
  );

  const playTrack = useCallback((trackId: string) => {
    setState((prev) => {
      const trackIndex = prev.tracks.findIndex((track) => track.submissionId === trackId);
      if (trackIndex === -1) {
        return prev;
      }

      const nextOrderIndex = prev.order.findIndex((idx) => idx === trackIndex);
      if (nextOrderIndex === -1) {
        const baseOrder = prev.isShuffled
          ? generateShuffleOrder(prev.tracks.length, trackIndex)
          : generateSequentialOrder(prev.tracks.length);
        return {
          ...prev,
          order: baseOrder,
          orderIndex: prev.isShuffled ? 0 : baseOrder.findIndex((idx) => idx === trackIndex),
          isPlaying: true,
        };
      }

      return {
        ...prev,
        orderIndex: nextOrderIndex,
        isPlaying: true,
      };
    });
  }, []);

  const nextTrack = useCallback((forcePlay?: boolean) => {
    setState((prev) => {
      if (prev.order.length === 0) {
        return prev;
      }
      const nextIndex = (prev.orderIndex + 1) % prev.order.length;
      return {
        ...prev,
        orderIndex: nextIndex,
        isPlaying: forcePlay ? true : prev.isPlaying,
      };
    });
  }, []);

  const previousTrack = useCallback((forcePlay?: boolean) => {
    setState((prev) => {
      if (prev.order.length === 0) {
        return prev;
      }
      const nextIndex = (prev.orderIndex - 1 + prev.order.length) % prev.order.length;
      return {
        ...prev,
        orderIndex: nextIndex,
        isPlaying: forcePlay ? true : prev.isPlaying,
      };
    });
  }, []);

  const setOrderIndex = useCallback((orderIndex: number, forcePlay?: boolean) => {
    setState((prev) => {
      if (prev.order.length === 0) return prev;
      const clamped = Math.max(0, Math.min(orderIndex, prev.order.length - 1));
      if (clamped === prev.orderIndex && (!forcePlay || prev.isPlaying)) return prev;
      return {
        ...prev,
        orderIndex: clamped,
        isPlaying: forcePlay ? true : prev.isPlaying,
      };
    });
  }, []);

  const togglePlay = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isPlaying: !prev.isPlaying,
    }));
  }, []);

  const setPlaying = useCallback((playing: boolean) => {
    setState((prev) => ({
      ...prev,
      isPlaying: playing,
    }));
  }, []);

  const toggleShuffle = useCallback(() => {
    setState((prev) => {
      if (prev.tracks.length === 0) {
        return prev;
      }

      const activeTrackIndex =
        prev.order.length > 0 ? prev.order[prev.orderIndex] ?? 0 : 0;

      if (!prev.isShuffled) {
        return {
          ...prev,
          isShuffled: true,
          order: generateShuffleOrder(prev.tracks.length, activeTrackIndex),
          orderIndex: 0,
        };
      }

      const sequentialOrder = generateSequentialOrder(prev.tracks.length);
      const newOrderIndex = sequentialOrder.findIndex((idx) => idx === activeTrackIndex);
      return {
        ...prev,
        isShuffled: false,
        order: sequentialOrder,
        orderIndex: newOrderIndex >= 0 ? newOrderIndex : 0,
      };
    });
  }, []);

  const updatePlaybackMeta = useCallback((meta: { progressSeconds?: number; durationSeconds?: number }) => {
    if (typeof meta.progressSeconds === "number") {
      setProgressSeconds(meta.progressSeconds);
    }
    if (typeof meta.durationSeconds === "number") {
      setDurationSeconds(meta.durationSeconds);
    }
  }, []);

  // Use useMemo with individual state properties instead of whole state object
  // This avoids object comparison issues that might cause initialization errors
  const value = useMemo<PlaybackContextValue>(
    () => ({
      state,
      currentTrack,
      setPlaylist,
      playTrack,
      nextTrack,
      previousTrack,
      setOrderIndex,
      togglePlay,
      setPlaying,
      toggleShuffle,
      progressSeconds,
      durationSeconds,
      updatePlaybackMeta,
    }),
    [
      // Depend on individual state properties instead of whole state object
      state.tracks,
      state.order,
      state.orderIndex,
      state.isPlaying,
      state.isShuffled,
      state.playlistId,
      state.playlistLabel,
      currentTrack,
      setPlaylist,
      playTrack,
      nextTrack,
      previousTrack,
      setOrderIndex,
      togglePlay,
      setPlaying,
      toggleShuffle,
      progressSeconds,
      durationSeconds,
      updatePlaybackMeta,
    ],
  );

  return <PlaybackContext.Provider value={value}>{children}</PlaybackContext.Provider>;
}

export function usePlayback() {
  const context = useContext(PlaybackContext);
  if (!context) {
    throw new Error('usePlayback must be used within PlaybackProvider');
  }
  return context;
}

